import React, { memo, MouseEvent, ReactNode, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { ChevronRight, Clock } from "lucide-react";
import { LanguageNameSpaces, TradeData } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { formatDetailDate } from "common/NewLook/formatDetailDate.ts";
import { ICON_TYPES } from "@shared/common/constants";
import IconWithFallback from "Global/IconWithFallback";
import { PriceLadderModel } from "common/NewLook/priceLadder.ts";
import {
  getNewLookTokens,
  NEW_LOOK_FIGURE_WEIGHT,
  NEW_LOOK_RADIUS,
} from "common/NewLook/tokens.ts";
import { NEW_LOOK_NARROW_PHONE_QUERY } from "common/NewLook/layout.ts";
import {
  getPhoneTradeDetails,
  PhoneDetailKey,
  PhoneSourceKind,
  PhoneTradeDetail,
  PhoneTradeSource,
} from "features/trades/NewLook/phoneTradeCardFigures.ts";
import {
  TRADE_CARD_ACCOUNT_ID,
  TRADE_CARD_AGE_ID,
  TRADE_CARD_DETAIL_ID,
  TRADE_CARD_DETAILS_BUTTON_ID,
  TRADE_CARD_DETAILS_ID,
  TRADE_CARD_SOURCE_ID,
  TRADE_CARD_STATUS_ID,
  TRADE_CARD_TIME_ID,
} from "testIds";

const ACCOUNT_ICON_SIZE = 16;
const CLOCK_ICON_SIZE = 14;
const NOTE_SEPARATOR = " · ";
const DETAIL_LABELS: Record<
  PhoneDetailKey,
  TranslationKeys<LanguageNameSpaces.TRADES>
> = {
  entry: "phone.detail_avg_entry",
  closed: "phone.detail_closed",
};

const SOURCE_LABELS: Record<
  PhoneSourceKind,
  TranslationKeys<LanguageNameSpaces.TRADES>
> = {
  dca: "phone.source_dca",
  grid: "phone.source_grid",
  hedging: "phone.source_hedging",
  trading_view: "phone.source_trading_view",
  signals: "phone.source_signals",
  manual: "phone.source_manual",
};

interface PhoneTradeDetailsProps {
  trade: TradeData;
  model: PriceLadderModel;
  isOpenTrades: boolean;
  formatPrice: (price: number) => string;
  caption?: string;
  duration?: string;
  quantity?: ReactNode;
  leverage?: string;
  onOpenDetails?: () => void;
}

const PhoneTradeDetails = ({
  trade,
  model,
  isOpenTrades,
  formatPrice,
  caption,
  duration,
  quantity,
  leverage,
  onOpenDetails,
}: PhoneTradeDetailsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const details = useMemo(
    () =>
      getPhoneTradeDetails(trade, model, isOpenTrades, {
        price: formatPrice,
        date: formatDetailDate,
      }),
    [trade, model, isOpenTrades, formatPrice]
  );
  return (
    <Details data-testid={TRADE_CARD_DETAILS_ID}>
      <FactGrid>
        {details.figures.map((detail) => (
          <DetailLine key={detail.key} detail={detail} />
        ))}
        {quantity && (
          <ExtraFact
            factKey="quantity"
            label={t("orders.quantity")}
            value={quantity}
          />
        )}
        {leverage && (
          <ExtraFact
            factKey="leverage"
            label={t("labels.leverage")}
            value={leverage}
          />
        )}
      </FactGrid>
      {(details.status || caption || details.note) && (
        <DetailNote
          status={details.status}
          caption={caption}
          note={details.note}
        />
      )}
      <Origin>
        {details.account && (
          <AccountLine exchange={trade.exchange} account={details.account} />
        )}
        {details.source.kind && (
          <SourceTag kind={details.source.kind} name={details.source.name} />
        )}
      </Origin>
      <Bottom>
        {details.opened && (
          <OpenedTime opened={details.opened} duration={duration} />
        )}
        {onOpenDetails && <OpenDetailsLink onOpen={onOpenDetails} />}
      </Bottom>
    </Details>
  );
};

export default memo(PhoneTradeDetails);

const ExtraFact = ({
  factKey,
  label,
  value,
}: {
  factKey: string;
  label: string;
  value: ReactNode;
}) => (
  <Fact data-testid={TRADE_CARD_DETAIL_ID} data-key={factKey}>
    <FactLabel>{label}</FactLabel> <FactValue>{value}</FactValue>
  </Fact>
);

const DetailLine = ({ detail }: { detail: PhoneTradeDetail }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  return (
    <Fact data-testid={TRADE_CARD_DETAIL_ID} data-key={detail.key}>
      <FactLabel>{t(DETAIL_LABELS[detail.key])}</FactLabel>{" "}
      <FactValue>
        {detail.value}
        {detail.count && (
          <FactCount>
            {NOTE_SEPARATOR}
            {detail.count}
          </FactCount>
        )}
      </FactValue>
    </Fact>
  );
};

const OpenedTime = ({
  opened,
  duration,
}: {
  opened: string;
  duration?: string;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const tokens = getNewLookTokens(useTheme());
  return (
    <Time
      data-testid={TRADE_CARD_TIME_ID}
      title={`${t("phone.detail_opened")} ${opened}`}
    >
      <Clock
        size={CLOCK_ICON_SIZE}
        strokeWidth={2}
        color={tokens.up}
        style={FLEX_NONE}
      />
      <TimeText>{opened}</TimeText>
      {duration && (
        <Duration data-testid={TRADE_CARD_AGE_ID}>{duration}</Duration>
      )}
    </Time>
  );
};

const SourceTag = ({
  kind,
  name,
}: PhoneTradeSource & { kind: PhoneSourceKind }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const text = name || t(SOURCE_LABELS[kind]);
  return (
    <Tag data-testid={TRADE_CARD_SOURCE_ID} title={text}>
      {text}
    </Tag>
  );
};

const AccountLine = ({
  exchange,
  account,
}: {
  exchange: string;
  account: string;
}) => (
  <Line data-testid={TRADE_CARD_ACCOUNT_ID} title={account}>
    <IconWithFallback
      coinOrExchangeName={exchange}
      iconType={ICON_TYPES.EXCHANGE}
      pxIconSize={ACCOUNT_ICON_SIZE}
    />
    <LineText>{account}</LineText>
  </Line>
);

const DetailNote = ({
  status,
  caption,
  note,
}: {
  status?: string;
  caption?: string;
  note?: string;
}) => {
  const noteText = [caption, note].filter(Boolean).join(NOTE_SEPARATOR);
  return (
    <Note>
      {status && (
        <NoteStatus data-testid={TRADE_CARD_STATUS_ID}>{status}</NoteStatus>
      )}
      {status && noteText && NOTE_SEPARATOR}
      {noteText}
    </Note>
  );
};

const OpenDetailsLink = ({ onOpen }: { onOpen: () => void }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const onClick = (event: MouseEvent) => {
    event.stopPropagation();
    onOpen();
  };
  return (
    <OpenLink
      type="button"
      onClick={onClick}
      data-testid={TRADE_CARD_DETAILS_BUTTON_ID}
    >
      {t("actions.trade_details")}
      <ChevronRight size={14} strokeWidth={2} />
    </OpenLink>
  );
};

const FLEX_NONE = { flex: "none" };

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const FactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  padding: 10px 12px;
  border-radius: ${NEW_LOOK_RADIUS.chip + 4}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  &:empty {
    display: none;
  }
`;
const Fact = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow-wrap: anywhere;
`;
const FactLabel = styled.span`
  font-size: 11.5px;
  line-height: 15px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const FactValue = styled.span`
  font-size: 13.5px;
  line-height: 18px;
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const FactCount = styled.span`
  font-weight: normal;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Origin = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: center;
  gap: 8px 12px;
  &:empty {
    display: none;
  }
`;
const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 13px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const LineText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Tag = styled.span`
  grid-column: 2;
  justify-self: end;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 2px 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  background: ${(p) => getNewLookTokens(p.theme).well};
`;
const NoteStatus = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Note = styled.span`
  min-width: 0;
  font-size: 12px;
  line-height: 16px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  overflow-wrap: anywhere;
`;
const Bottom = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 12px;
  min-height: 20px;
  &:empty {
    display: none;
  }
  @media ${NEW_LOOK_NARROW_PHONE_QUERY} {
    column-gap: 8px;
  }
`;
const Time = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 13px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  @media ${NEW_LOOK_NARROW_PHONE_QUERY} {
    gap: 5px;
    font-size: 12.5px;
  }
`;
const TimeText = styled.span`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const Duration = styled.span`
  flex: none;
  padding: 1px 7px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  font-size: 11.5px;
  line-height: 15px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  background: ${(p) => getNewLookTokens(p.theme).well};
  @media ${NEW_LOOK_NARROW_PHONE_QUERY} {
    padding: 1px 6px;
  }
`;
const OpenLink = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: -8px -4px -8px auto;
  padding: 8px 4px;
  border: 0;
  background: none;
  font: inherit;
  font-size: 12.5px;
  line-height: 16px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  @media ${NEW_LOOK_NARROW_PHONE_QUERY} {
    font-size: 12px;
  }
`;
